"use client";

import { useEffect, useState } from "react";
import { format } from "date-fns";
import { MdDownload } from "react-icons/md";
import { SiMicrosoftexcel } from "react-icons/si";
import { useConciliationEmailsFilterActions } from "../filter/hooks/actions";
import { useFilterState } from "../filter/hooks/filterState";
import { ConciliationEmailReportType } from "./conciliation-email-report-types";
import EmailReportFilters from "./email-report-filters-sheet";
import EmailReportTabs from "./email-report-tabs";
import EmailReportToolbarFilters from "./email-report-toolbar-filters";
import { useExportEmailReportExcelMutation } from "@/services/conciliation/email-report";
import { downloadExcelBlob } from "@/utils/download-excel";
import { toast } from "components/ui/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { handeOrderBy } from "@/utils/pagination";
import { EMAIL_REPORT_MESSAGES } from "../utils/messages";

const EXPORT_FILTER_KEYS = [
  'storeCode',
  'regional',
  'operator',
  'date',
  'sent_at',
  'storeAllowedToReceiveEmails',
  'type',
  'is_finalized',
  'from_date',
  'to_date',
  'status',
  'aggregatedStatus',
  'responsible',
  'dateType',
] as const;

const buildExportFilters = (filters: any) => {
  const definedFilters: Record<string, any> = {};

  EXPORT_FILTER_KEYS.forEach(key => {
    if (filters?.[key] !== undefined) {
      definedFilters[key] = filters[key];
    }
  });

  const orderBy = handeOrderBy(filters?.order);

  return {
    search: filters?.search,
    reportType: filters?.reportType || ConciliationEmailReportType.EMAILS_BY_STORE,
    ...definedFilters,
    orderBy: JSON.stringify(orderBy),
  };
};

const buttonClasses = {
  linkButton: "inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-0",
  linkButtonEnabled: "text-gray-700 hover:bg-gray-100",
  linkButtonDisabled: "text-gray-400 cursor-not-allowed",
  buttonIcon: "w-4 h-4",
};

export function DataTableToolbar() {
  const { updateFilter } = useConciliationEmailsFilterActions();

  const { filters } = useFilterState();
  const [listType, setListType] = useState<ConciliationEmailReportType>(
    (filters?.reportType as ConciliationEmailReportType) || ConciliationEmailReportType.EMAILS_BY_STORE
  );

  const [exportExcel, { isLoading: isExportingExcel }] = useExportEmailReportExcelMutation();

  useEffect(() => {
    if (filters?.reportType && filters.reportType !== listType) {
      setListType(filters.reportType as ConciliationEmailReportType);
    }
  }, [filters?.reportType]);

  const handleTypeChange = (value: ConciliationEmailReportType) => {
    setListType(value);
    updateFilter({ key: "reportType", value });
  };

  const handleExportExcel = async () => {
    try {
      const exportFilters = buildExportFilters(filters);
      const response = await exportExcel(exportFilters).unwrap();
      const fileName = `relatorio-emails-enviados-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      downloadExcelBlob(response, fileName);
    } catch (error) {
      toast(EMAIL_REPORT_MESSAGES.EXPORT.ERROR);
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <EmailReportTabs listType={listType} onTypeChange={handleTypeChange} />
      <div className="flex items-end gap-2 w-full justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
          <EmailReportToolbarFilters listType={listType} />
        </div>
        <div className="flex items-end gap-2 justify-end sm:mt-0">
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  buttonClasses.linkButton,
                  isExportingExcel ? buttonClasses.linkButtonDisabled : buttonClasses.linkButtonEnabled
                )}
                disabled={isExportingExcel}
              >
                <MdDownload className={buttonClasses.buttonIcon} />
                {isExportingExcel ? "Exportando..." : "Exportar Relatório"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2">
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleExportExcel}
                  disabled={isExportingExcel}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-0"
                >
                  <SiMicrosoftexcel className="w-4 h-4 text-green-600" />
                  Planilha Excel
                </button>
              </div>
            </PopoverContent>
          </Popover>
          <EmailReportFilters />
        </div>
      </div>
    </div>
  );
}
